import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { FeedbackCategory, FeedbackStatus, SatisfactionLevel } from "@verity/database/prisma";
import apiClient from "../../../helpers/api-client";
import { PaginationInfo } from "../../../types/general-types";

export type FeedbackResponse = {
	results: {
		id: number;
		userId: number;
		satisfaction: SatisfactionLevel;
		category: FeedbackCategory;
		summary: string;
		details: string;
		status: FeedbackStatus;
		createdAt: string;
		updatedAt: string;
		email: string;
		name: string;
		institutionId: number;
		institutionName: string;
	}[];
} & PaginationInfo;

type FetchFeedbackOptions = {
	satisfaction?: SatisfactionLevel;
	category?: FeedbackCategory;
	status?: FeedbackStatus;
	text?: string;
	sortColumns?: { column: keyof FeedbackResponse["results"][number]; direction: "asc" | "desc" }[];
	page: number;
	size: number;
};

type UpdateFeedbackStatus = {
	id: number;
	status: FeedbackStatus;
};

const feedbackApi = createApi({
	reducerPath: "feedback",
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: `/api/verity/feedback`,
		})(arg, api, extraOptions),
	tagTypes: ["Feedback"],
	endpoints: (builder) => ({
		getFeedback: builder.query<FeedbackResponse, FetchFeedbackOptions>({
			query: (args) => {
				const searchParams = new URLSearchParams();
				Object.entries(args).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== "") {
						if (key === "sortColumns" && Array.isArray(value)) {
							searchParams.set(key, JSON.stringify(value));
						} else {
							searchParams.set(key, String(value));
						}
					}
				});

				return `?${searchParams.toString()}`;
			},
			providesTags: (result) =>
				result
					? [...result.results.map(({ id }) => ({ type: "Feedback" as const, id })), { type: "Feedback", id: "LIST" }]
					: [{ type: "Feedback" as const, id: "LIST" }],
		}),
		updateFeedbackStatus: builder.mutation<void, UpdateFeedbackStatus>({
			query: ({ id, status }) => ({
				url: `/${id}`,
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ status: status }),
			}),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: "Feedback", id },
				{ type: "Feedback", id: "LIST" },
			],
		}),
		removeFeedback: builder.mutation<void, number>({
			query: (id) => ({
				url: `/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: [{ type: "Feedback", id: "LIST" }],
		}),
	}),
});

export default feedbackApi;
